import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, ModalBody } from "reactstrap";
import styled from "styled-components";
import moment from "moment";
import { api } from "../api/api";

const INTERVAL_MS = 10000;

const NoticeModal = styled(Modal)`
  max-width: 440px;

  .modal-content {
    border: none;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 28px 70px rgba(53, 0, 95, 0.22);
  }
`;

const Stripe = styled.div`
  height: 6px;
  background: linear-gradient(90deg, #d0021b 0%, #f6a51f 100%);
`;

const Body = styled.div`
  padding: 28px 28px 24px;
  text-align: center;
  background: #fff;
`;

const IconWrap = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(208, 2, 27, 0.08);
  color: #d0021b;
`;

const Title = styled.h3`
  margin: 0 0 14px;
  color: #3a3b3f;
  font-size: 22px;
  font-weight: 700;
`;

const DateChip = styled.div`
  display: inline-block;
  margin-bottom: 16px;
  padding: 8px 14px;
  border-radius: 999px;
  background: #f5f7f8;
  color: #4d4467;
  font-weight: 700;
  letter-spacing: 0.01em;
`;

const Copy = styled.p`
  margin: 0;
  color: #5c5e66;
  font-size: 15px;
  line-height: 1.55;
`;

const Reminder = styled.div`
  margin-top: 18px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(246, 165, 31, 0.12);
  color: #7a4b00;
  font-size: 13px;
  line-height: 1.45;
`;

const SkipButton = styled.button`
  margin-top: 20px;
  min-width: 140px;
  padding: 11px 22px;
  border-radius: 10px;
  border: 1px solid #4d4467;
  background: #fff;
  color: #4d4467;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    background: #4d4467;
    color: #fff;
  }
`;

export const SubscriptionExpiryPopup = () => {
  const loginFlag = useSelector((state) => state.login.loginFlag);
  const [open, setOpen] = useState(false);
  const [expiryDate, setExpiryDate] = useState(null);
  const timerRef = useRef(null);
  const skippedUntilRef = useRef(0);
  const checkRef = useRef(async () => { });

  useEffect(() => {
    if (!loginFlag) {
      setOpen(false);
      return undefined;
    }

    let cancelled = false;

    const schedule = (delay) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        checkRef.current();
      }, delay);
    };

    const check = async () => {
      const wait = skippedUntilRef.current - Date.now();
      if (wait > 0) {
        schedule(wait);
        return;
      }

      try {
        const res = await api("gym/subscription-notice", {}, "get");
        if (cancelled) return;

        const stillWaiting = skippedUntilRef.current - Date.now();
        if (stillWaiting > 0) {
          schedule(stillWaiting);
          return;
        }

        const data = res && res.status === 200 ? res.data.data : null;
        if (!data || !data.show) {
          setOpen(false);
          schedule(INTERVAL_MS);
          return;
        }

        setExpiryDate(data.expiryDate);
        setOpen(true);
        schedule(INTERVAL_MS);
      } catch (error) {
        if (!cancelled) schedule(INTERVAL_MS);
      }
    };

    checkRef.current = check;
    check();

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
  }, [loginFlag]);

  const skip = () => {
    skippedUntilRef.current = Date.now() + INTERVAL_MS;
    setOpen(false);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      skippedUntilRef.current = 0;
      checkRef.current();
    }, INTERVAL_MS);
  };

  const formattedDate = expiryDate
    ? moment(Number(expiryDate)).format("Do [of] MMMM YYYY")
    : "";

  return (
    <NoticeModal
      isOpen={open}
      centered
      backdrop="static"
      keyboard={false}
      fade
    >
      <ModalBody className="p-0">
        <Stripe />
        <Body>
          <IconWrap aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 9v4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="12" cy="16.5" r="1" fill="currentColor" />
              <path
                d="M10.3 4.8 2.8 17.4A2 2 0 0 0 4.5 20.4h15a2 2 0 0 0 1.7-3L13.7 4.8a2 2 0 0 0-3.4 0Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
          </IconWrap>
          <Title>Subscription expired</Title>
          <DateChip>{formattedDate}</DateChip>
          <Copy>
            Your subscription expired on {formattedDate}. Some features may be
            limited until you renew. Renew now to restore full access and avoid
            losing service.
          </Copy>

          <SkipButton type="button" onClick={skip}>
            Skip
          </SkipButton>
        </Body>
      </ModalBody>
    </NoticeModal>
  );
};
